import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { LucideIcon } from "lucide-react";
import CustomDatePicker from './CustomDatePicker';
import { format } from 'date-fns';
import { parseCalendarDate, parseLocalDate } from '../../../utilities/dateTime';

const DATE_PICKER_OPEN_EVENT = 'custom-date-picker:open';

interface CustomInputProps {
    ariaLabel?: string;
    ariaInvalid?: boolean;
    ariaDescribedBy?: string;
    label?: string;
    name: string;
    placeholder?: string;
    value: string;
    type?: "text" | "date" | "time" | "textarea" | "password" | "email" | "number";
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    error?: string;
    warning?: string; 
    icon?: LucideIcon;
    min?: string;
    max?: string;
    step?: string;
    borderBotttom?: boolean;
    disabled?: boolean;
    disableFuture?: boolean
    disablePast?: boolean
    onDateDraftChange?: (valid: boolean) => void;
    dateYearRange?: { from: number; to: number };
}

const CustomInput: React.FC<CustomInputProps> = ({
    dateYearRange,
    ariaLabel,
    ariaInvalid,
    ariaDescribedBy,
    label,
    name,
    placeholder,
    value,
    type = "text",
    onChange,
    error,
    warning,
    icon: Icon,
    borderBotttom,
    min,
    max,
    step,
    disabled,
    disableFuture,
    disablePast,
    onDateDraftChange
}) => {
    const isTextarea = type === "textarea";
    const isDate = type === "date";
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState("");
    
    const wrapperRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const instanceIdRef = useRef(`${name}-${Math.random().toString(36).slice(2)}`);
    const [popoverPosition, setPopoverPosition] = useState({
        top: 0,
        left: 0,
        width: 320,
        maxHeight: 360,
        placement: "bottom" as "bottom" | "top",
    });

    useEffect(() => {
        if (isDate && value) {
            const formatted = parseCalendarDate(value, true) ? format(parseCalendarDate(value, true)!, "MM-dd-yyyy") : "Invalid date";
            if (formatted !== "Invalid date") {
                setInputValue(formatted);
            }
        } else if (isDate && !value) {
            setInputValue("");
        }
    }, [value, isDate]);

    // Keep only one shared calendar open and reliably close it when focus moves elsewhere.
    useEffect(() => {
        const handlePointerDownOutside = (event: PointerEvent) => {
            const target = event.target as Node;

            const clickedTrigger =
                wrapperRef.current?.contains(target) ?? false;

            const clickedCalendar =
                dropdownRef.current?.contains(target) ?? false;

            if (!clickedTrigger && !clickedCalendar) {
                setOpen(false);
            }
        };

        const handleAnotherPickerOpen = (event: Event) => {
            const pickerEvent = event as CustomEvent<{ id: string }>;
            if (pickerEvent.detail?.id !== instanceIdRef.current) {
                setOpen(false);
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") setOpen(false);
        };

        const handleWindowBlur = () => setOpen(false);

        // Capture phase prevents dropdowns that stop propagation from leaving the calendar open.
        document.addEventListener("pointerdown", handlePointerDownOutside, true);
        document.addEventListener(DATE_PICKER_OPEN_EVENT, handleAnotherPickerOpen);
        document.addEventListener("keydown", handleKeyDown);
        window.addEventListener("blur", handleWindowBlur);

        return () => {
            document.removeEventListener("pointerdown", handlePointerDownOutside, true);
            document.removeEventListener(DATE_PICKER_OPEN_EVENT, handleAnotherPickerOpen);
            document.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("blur", handleWindowBlur);
        };
    }, []);

    const openDatePicker = () => {
        if (disabled) return;

        document.dispatchEvent(
            new CustomEvent(DATE_PICKER_OPEN_EVENT, {
                detail: { id: instanceIdRef.current },
            })
        );
        setOpen(true);
    };

    const updatePopoverPosition = useCallback(() => {
        if (!open || !wrapperRef.current) return;

        const triggerRect = wrapperRef.current.getBoundingClientRect();
        const calendarRect = dropdownRef.current?.firstElementChild?.getBoundingClientRect();
        const measuredWidth = calendarRect?.width || dropdownRef.current?.offsetWidth || 320;
        const measuredHeight = calendarRect?.height || dropdownRef.current?.offsetHeight || 360;

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const edgePadding = 12;
        const gap = 8;

        let left = triggerRect.left;

        // Keep the popup inside the horizontal viewport.
        if (left + measuredWidth > viewportWidth - edgePadding) {
            left = viewportWidth - measuredWidth - edgePadding;
        }

        if (left < edgePadding) {
            left = edgePadding;
        }

        // Reserve both the trigger gap and viewport edge before choosing a side.
        // On short viewports, scroll the calendar on that side rather than clamping
        // its full natural height across the triggering input.
        const spaceBelow = Math.max(0, viewportHeight - triggerRect.bottom - gap - edgePadding);
        const spaceAbove = Math.max(0, triggerRect.top - gap - edgePadding);
        const shouldOpenAbove = spaceBelow < measuredHeight && spaceAbove > spaceBelow;
        const availableHeight = shouldOpenAbove ? spaceAbove : spaceBelow;
        const visibleHeight = Math.min(measuredHeight, availableHeight);
        const top = shouldOpenAbove
            ? triggerRect.top - gap - visibleHeight
            : triggerRect.bottom + gap;
        setPopoverPosition({
            top,
            left,
            width: measuredWidth,
            maxHeight: availableHeight,
            placement: shouldOpenAbove ? "top" : "bottom",
        });
    }, [open]);

    useEffect(() => {
        if (!open) return;

        // Position once before/after the portal paints so measured calendar
        // dimensions are available without changing modal layout.
        updatePopoverPosition();

        const frame = window.requestAnimationFrame(updatePopoverPosition);
        const secondFrame = window.requestAnimationFrame(() => {
            window.requestAnimationFrame(updatePopoverPosition);
        });

        const calendar = dropdownRef.current?.firstElementChild;
        const sizeObserver = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(updatePopoverPosition);
        if (calendar) sizeObserver?.observe(calendar);

        const handleViewportChange = () => {
            updatePopoverPosition();
        };

        // Capture-phase scroll catches scrolling modal bodies as well as window
        // scrolling. Because the popover is fixed to the viewport, recalculate
        // its anchor position rather than expanding the modal.
        window.addEventListener("resize", handleViewportChange);
        document.addEventListener("scroll", handleViewportChange, true);

        return () => {
            sizeObserver?.disconnect();
            window.cancelAnimationFrame(frame);
            window.cancelAnimationFrame(secondFrame);
            window.removeEventListener("resize", handleViewportChange);
            document.removeEventListener("scroll", handleViewportChange, true);
        };
    }, [open, updatePopoverPosition]);

    const handleDateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let val = e.target.value.replace(/\D/g, "");
        if (val.length > 8) val = val.slice(0, 8);

        let formatted = val;
        if (val.length > 2 && val.length <= 4) {
            formatted = `${val.slice(0, 2)}-${val.slice(2)}`;
        } else if (val.length > 4) {
            formatted = `${val.slice(0, 2)}-${val.slice(2, 4)}-${val.slice(4)}`;
        }

        setInputValue(formatted);

        if (!formatted) {
            onDateDraftChange?.(true);
            onChange({ target: { name, value: "" } } as React.ChangeEvent<HTMLInputElement>);
            return;
        }

        if (formatted.length === 10) {
            const dateObj = parseLocalDate(formatted, "MM-dd-yyyy");
            const isoDate = dateObj ? format(dateObj, "yyyy-MM-dd") : "";
            if (dateObj && (!min || isoDate >= min) && (!max || isoDate <= max)) {
                onDateDraftChange?.(true);
                onChange({ target: { name, value: isoDate } } as React.ChangeEvent<HTMLInputElement>);
                return;
            }
        }
        onDateDraftChange?.(false);
    };

    return (
        <div className="flex flex-col gap-1.5 w-full">
            {label && (
                <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest ml-1">
                    {label}
                </label>
            )}

            <div
                ref={wrapperRef}
                className={`custom-input-control relative flex items-center overflow-hidden bg-gray-50/50 border rounded-xl transition-all duration-200 group
                    ${isTextarea ? "min-h-[100px] items-start pt-3" : "h-12"}
                    ${error ? "border-red-300 bg-red-50/30" : warning ? "border-amber-300 bg-amber-50/30" : "border-gray-200 hover:border-gray-300 focus-within:border-[#7E57C2] focus-within:ring-4 focus-within:ring-[#7E57C2]/10 focus-within:bg-white"}
                    ${disabled ? "opacity-60 cursor-not-allowed bg-gray-100" : ""}
                `}
            >
                {Icon && (
                    <Icon
                        size={18}
                        aria-hidden="true"
                        className={`custom-input-icon pointer-events-none absolute left-3.5 z-10 transition-colors ${isTextarea ? "top-3.5" : ""}
                        ${error ? "text-red-400" : warning ? "text-amber-400" : "text-gray-400 group-focus-within:text-[#7E57C2]"}`}
                    />
                )}

                {isTextarea ? (
                    <textarea
                        name={name}
                        aria-label={ariaLabel}
                            aria-invalid={ariaInvalid}
                            aria-describedby={ariaDescribedBy}
                        placeholder={placeholder}
                        value={value}
                        onChange={onChange}
                        disabled={disabled}
                        className="w-full h-full min-h-[100px] bg-transparent border-none outline-none focus:ring-0 text-sm md:text-base text-gray-700 placeholder-gray-400 resize-none custom-scrollbar"
                        style={{ paddingLeft: Icon ? "2.75rem" : "1rem", paddingTop: "0.1rem" }}
                    />
                ) : isDate ? (
                    <div className="custom-date-input-surface w-full h-full relative">
                        <input
                            type="text"
                            aria-label={ariaLabel}
                            aria-invalid={ariaInvalid}
                            aria-describedby={ariaDescribedBy}
                            value={inputValue}
                            onChange={handleDateInputChange}
                            onFocus={openDatePicker}
                            placeholder={placeholder || "MM-DD-YYYY"}
                            disabled={disabled}
                            className="w-full h-full rounded-[inherit] bg-transparent border-none outline-none focus:ring-0 text-sm md:text-base text-gray-700 placeholder-gray-400"
                            style={{ paddingLeft: Icon ? "2.75rem" : "1rem" }}
                        />
                        {open && !disabled && typeof document !== "undefined" && createPortal(
                            <div
                                ref={dropdownRef}
                                className={`fixed z-[1000000] animate-in fade-in ${
                                    popoverPosition.placement === "top"
                                        ? "slide-in-from-bottom-2"
                                        : "slide-in-from-top-2"
                                }`}
                                style={{
                                    top: popoverPosition.top,
                                    left: popoverPosition.left,
                                    maxHeight: popoverPosition.maxHeight,
                                    overflowY: "auto",
                                    overflowX: "hidden",
                                    overscrollBehavior: "contain",
                                }}
                                data-custom-date-picker-portal="true"
                            >
                                <CustomDatePicker
                                    yearRange={dateYearRange}
                                    value={value}
                                    onChange={(date) => {
                                        onDateDraftChange?.(true);
                                        onChange({ target: { name, value: date } } as any);
                                        setOpen(false);
                                    }}
                                    disabled={disabled}
                                    disableFuture={disableFuture}
                                    disablePast={disablePast}
                                    min={min}
                                    max={max}
                                />
                            </div>,
                            document.body
                        )}
                    </div>
                ) : (
                    <input
                        type={type}
                        name={name}
                        aria-label={ariaLabel}
                            aria-invalid={ariaInvalid}
                            aria-describedby={ariaDescribedBy}
                        placeholder={placeholder}
                        value={value}
                        min={min}
                        max={max}
                        step={step}
                        onChange={onChange}
                        disabled={disabled}
                        className="w-full h-full bg-transparent border-none outline-none focus:ring-0 text-sm md:text-base text-gray-700 placeholder-gray-400"
                        style={{ paddingLeft: Icon ? "2.75rem" : "1rem" }}
                    />
                )}
            </div>

            {error && (
                <div className="flex items-center gap-1 mt-1 ml-1 text-red-500 animate-in slide-in-from-left-1">
                    <span className="text-[11px] font-medium tracking-tight">{error}</span>
                </div>
            )}

            {!error && warning && (
                <div className="flex items-center gap-1 mt-1 ml-1 text-amber-500 animate-in slide-in-from-left-1">
                    <span className="text-[11px] font-medium tracking-tight">{warning}</span>
                </div>
            )}
        </div>
    );
};

export default CustomInput;
